import { Search, X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';

interface SettingsFindBarProps {
  value: string;
  onChange: (value: string) => void;
}

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

/**
 * Filters the directory as you type. "/" focuses it from anywhere on the
 * page; Escape clears it.
 */
export const SettingsFindBar: React.FC<SettingsFindBarProps> = ({
  value,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || isTypingTarget(event.target)) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="relative">
      <Search
        size={16}
        aria-hidden
        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
      />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            onChange('');
            inputRef.current?.blur();
          }
        }}
        placeholder="Find a setting — try “bill printer”, “TIN” or “passcode”"
        aria-label="Find a setting"
        className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-12 text-sm text-gray-900 caret-pos-primary placeholder:text-gray-500 focus:border-pos-primary focus:outline-none focus:ring-2 focus:ring-pos-primary/20 [&::-webkit-search-cancel-button]:hidden"
      />
      {value ? (
        <button
          type="button"
          onClick={() => {
            onChange('');
            inputRef.current?.focus();
          }}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pos-primary"
        >
          <X size={14} aria-hidden />
        </button>
      ) : (
        <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-sans text-xs text-gray-500 sm:block">
          /
        </kbd>
      )}
    </div>
  );
};
